import {
  type Dispatch,
  type SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Wrapper } from '@googlemaps/react-wrapper';
import clsx from 'clsx';
import type { LatLng, LatLngLiteral, Map } from 'leaflet';
import { createPortal } from 'react-dom';

import type { IGeoDirection } from '@quick/go-api-types/geo/direction';
import type { ICity } from '@quick/go-api-types/services/cities';
import type { ICountry } from '@quick/go-api-types/services/countries';
import type { ILine } from '@quick/go-api-types/services/lines';

import { useAppTranslation } from '@/app/i18n/client';
import mapPin from '@/assets/mapIcons/map-pin.png';
import { LeafletMap } from '@/components/core';
import { useLeafletMapContext } from '@/components/core/LeafletContainer';
import { ReactSelect as RSelect } from '@/components/core/ReactSelect';
import { envs } from '@/layers/core/data/const';
import { defaultCities, points } from '@/layers/core/data/country';
import { useGeneralStore } from '@/layers/core/deal/repositories/generalStore';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { useDebounce } from '@/layers/core/deal/services/useDebounce';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { idToCountry } from '@/layers/core/domain/countryMap';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

export const SelectPoint = forwardRef<
  Map,
  {
    onMoveEnd?: (c: LatLng, city_id?: number) => void;
    onGetSearch?: ({
      cityId,
      address,
    }: {
      cityId?: number;
      address?: string;
    }) => void;
    className?: string;
    center?: LatLngLiteral;
    defaultText?: string;
    defaultCityID?: number;
    defaultCountryID?: number;
    enableSearch?: boolean;
    onAction?: (c: LatLng) => Promise<void> | void;
    onCancel?: () => void;
    limitCountriesByLine?: number;
    readOnlyCountry?: boolean;
  } & (
    | {
        onVerify?: (c: LatLng, address: string) => Promise<void> | void;
        onCenter?: never;
      }
    | {
        onVerify?: never;
        onCenter?: (c: LatLng) => void;
      }
  )
>(function SelectPoint(
  {
    onVerify,
    onMoveEnd,
    onGetSearch,
    className,
    center,
    defaultText,
    defaultCityID,
    defaultCountryID,
    enableSearch,
    onAction,
    onCancel,
    limitCountriesByLine,
    readOnlyCountry,
    onCenter,
  },
  ref
) {
  const searchState = useState(defaultText ?? '');
  const { country } = useCountry();
  const point = { ...points[country].center };
  const c = Object.assign(point, center);
  const pointState = useState(c);
  return (
    <LeafletMap
      ref={ref}
      className={className}
      center={c}
      boxZoom
      zoom={8}
      gestureHandling
      scrollWheelZoom="center"
      zoomControl={false}
    >
      <Zoom />
      <SelfCenterMarker />
      <LocateControl />
      <SearchBox
        enableSearch={enableSearch}
        onAction={onAction}
        defaultCityID={defaultCityID}
        defaultCountryID={defaultCountryID}
        addressState={searchState}
        pointState={pointState}
        limitCountriesByLine={limitCountriesByLine}
        readOnlyCountry={readOnlyCountry}
        onGetSearch={onGetSearch}
      />
      <CenterMoveDetector onMoveEnd={onMoveEnd} />
      <Wrapper apiKey={envs.mapsApiKey} id="gmapsGeocoding">
        <Verifier
          searchState={searchState}
          pointState={pointState}
          onVerify={onVerify}
          onCenter={onCenter}
          onCancel={onCancel}
        />
      </Wrapper>
    </LeafletMap>
  );
});

const Zoom = () => {
  const { RL } = useLeafletMapContext();
  return <RL.ZoomControl position="bottomright" />;
};

const SelfCenterMarker = () => {
  const { RL, L } = useLeafletMapContext();
  const map = RL.useMap();
  useEffect(() => {
    const m = L.marker(map.getCenter(), {
      icon: new L.Icon({
        iconUrl: mapPin.src,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
      }),
      zIndexOffset: 20,
    }).addTo(map);
    const el = () => {
      m.setLatLng(map.getCenter());
    };
    map.addEventListener('move', el);
    return () => {
      map.removeEventListener('move', el);
      m.remove();
    };
  }, [map, L]);

  return null;
};

const CenterMoveDetector = ({
  onMoveEnd,
}: {
  onMoveEnd?: (c: LatLng) => void;
}) => {
  const { RL } = useLeafletMapContext();
  const { useMapEvent, useMap } = RL;
  const map = useMap();
  useMapEvent(
    'moveend',
    useCallback(() => onMoveEnd?.(map.getCenter()), [onMoveEnd, map])
  );

  return null;
};

const Verifier = ({
  searchState,
  pointState,
  onVerify,
  onCancel,
  onCenter,
}: {
  onVerify?: (c: LatLng, address: string) => Promise<void> | void;
  onCenter?: (c: LatLng) => void;
  searchState: [string, Dispatch<SetStateAction<string>>];
  pointState: [LatLngLiteral, Dispatch<SetStateAction<LatLngLiteral>>];
  onCancel?: () => void;
}) => {
  const { RL } = useLeafletMapContext();
  const { useMap } = RL;
  const map = useMap();

  const blNode = useMemo(
    () =>
      Object.values(
        Object.values(map.getContainer().childNodes).find(
          (node) =>
            (node as HTMLElement).className === 'leaflet-control-container'
        )?.childNodes ?? {}
      ).find(
        (node) =>
          (node as HTMLElement).className === 'leaflet-bottom leaflet-left'
      ),
    [map]
  );

  const [verifying, setVerifying] = useState(false);

  const [search, setSearch] = searchState;

  const [geocoder] = useState(new google.maps.Geocoder());

  const [point] = pointState;

  const { t } = useAppTranslation(['common']);
  if (onVerify || onCenter)
    return createPortal(
      <>
        {onCancel && (
          <button
            className="bg-gray-light-2 pointer-events-auto z-[800] mb-[10px] ml-[10px] rounded p-2
              font-bold shadow max-sm:!mb-7"
            onClick={onCancel}
            type="button"
          >
            {t('common:cancel')}
          </button>
        )}
        <button
          className="bg-brand pointer-events-auto z-[800] mb-[10px] ml-[10px] rounded p-2 font-bold
            shadow max-sm:!mb-7"
          type="button"
          onClick={async () => {
            const coords = map.getCenter();
            if (onCenter) return onCenter(coords);
            if (!onVerify) return;
            try {
              setVerifying(true);
              const movedTooMuch =
                Math.abs(coords.lat - point.lat) +
                  Math.abs(coords.lng - point.lng) >
                0.0005; // aprox 50m
              const res =
                !search || movedTooMuch
                  ? await geocoder.geocode({ location: coords })
                  : null;
              const gAddr = res?.results?.at?.(0)?.formatted_address || search;
              setSearch(gAddr);
              await onVerify(coords, gAddr);
            } finally {
              setVerifying(false);
            }
          }}
          disabled={verifying}
        >
          {!verifying ? (
            t('common:select')
          ) : (
            <div className="aspect-square w-4 animate-spin rounded-full border-4 border-dotted" />
          )}
        </button>
      </>,
      blNode as Element,
      'verifyPortal'
    );
  return null;
};

const LocateControl = () => {
  const { RL, L } = useLeafletMapContext();
  const { useMap } = RL;
  const map = useMap();
  const locateRef = useRef(
    L.control.locate({
      position: 'bottomright',
      icon: "inline-block bg-[url('/locate.svg')] bg-contain bg-no-repeat bg-bottom w-4 h-4 m-[7px] [.leaflet-control-locate.active_&]:bg-[url('/locate_s.svg')]",
      iconLoading:
        'inline-block border-4 border-dotted rounded-full w-4 h-4 m-[7px] animate-spin border-gray-500',
      drawCircle: false,
    })
  );
  useEffect(() => {
    const ref = locateRef.current;
    ref.addTo(map);
    return () => {
      ref.remove();
    };
  }, [map]);
  return null;
};

const SearchBox = ({
  enableSearch,
  onGetSearch,
  onAction,
  defaultCityID,
  defaultCountryID,
  addressState,
  pointState,
  limitCountriesByLine,
  readOnlyCountry,
}: {
  enableSearch?: boolean;
  // onSearch?(q: { address: string; city_id: number }): Promise<void>;
  onGetSearch?: ({
    cityId,
    address,
  }: {
    cityId?: number;
    address?: string;
  }) => void;
  onAction?: (c: LatLng) => Promise<void> | void;
  addressState: [string, Dispatch<SetStateAction<string>>];
  pointState: [LatLngLiteral, Dispatch<SetStateAction<LatLngLiteral>>];
  defaultCityID?: number;
  defaultCountryID?: number;
  limitCountriesByLine?: number;
  readOnlyCountry?: boolean;
}) => {
  const { RL } = useLeafletMapContext();
  const [, setRe] = useState(false);
  useEffect(() => {
    setRe(true);
  }, []);

  const map = RL.useMap();

  const { id, country } = useCountry();
  const country_id = (defaultCountryID as 1 | 2 | 3 | 4) ?? id;
  const { data: allCountries } = useFetch<ICountry[]>(
    enableSearch && { url: '/services/countries', immutable: true }
  );
  const { data: line } = useFetch<ILine>(
    !!limitCountriesByLine &&
      enableSearch && {
        url: `/services/lines/${limitCountriesByLine}`,
        immutable: true,
      }
  );
  const countries = useMemo(
    () => (limitCountriesByLine ? line?.countries : allCountries),
    [allCountries, line, limitCountriesByLine]
  );

  const defaultCountry = useMemo(
    () => countries?.find((c) => c.id === country_id),
    [country_id, countries]
  );

  const [selectedCountry, setSelectedCountry] = useState<1 | 2 | 3 | 4>(
    country_id
  );
  const [citySearch, setCitySearch] = useState('');
  const debouncedSearch = useDebounce(citySearch.trim(), 1_000);
  const { data: cities } = useFetch<ICity[]>(
    enableSearch &&
      debouncedSearch.length >= 3 && {
        url: '/services/cities',
        params: {
          city: debouncedSearch,
          country_id: selectedCountry,
          active: true,
        },
      }
  );

  const defaultCityBySelectedCountry =
    defaultCities?.[idToCountry[selectedCountry]] || 1;

  // Default city data
  const { data: defaultCity } = useFetch<ICity[]>(
    enableSearch && {
      url: '/services/cities',
      params: {
        city_id:
          selectedCountry === country_id
            ? defaultCityID || defaultCityBySelectedCountry
            : defaultCityBySelectedCountry,
      },
    }
  );

  const [cityId, setCityId] = useState<number | undefined>();
  const [search, setSearch] = addressState;
  const { t } = useAppTranslation(['common']);
  const setAlert = useGeneralStore((store) => store.setAlert);

  const tlNode = useMemo(
    () =>
      Object.values(
        Object.values(map.getContainer().childNodes).find(
          (node) =>
            (node as HTMLElement).className === 'leaflet-control-container'
        )?.childNodes ?? {}
      ).find(
        (node) => (node as HTMLElement).className === 'leaflet-top leaflet-left'
      ),
    [map]
  );

  const [, setPoint] = pointState;

  useEffect(() => {
    if (!cityId && !!defaultCity?.at(0))
      onGetSearch?.({ cityId: defaultCity?.at(0)?.id, address: search });
  }, [cityId, defaultCity, onGetSearch, search]);

  return createPortal(
    <form
      className={clsx(
        `focus-within:[&>*]:outline-brand pointer-events-auto z-[800] ml-[10px] mt-[10px]
        flex max-w-full flex-row flex-wrap sm:flex-nowrap`,
        `[&>*:last-child]:border-br sm:[&>*:last-child]:border-r
        sm:[&>*:not(:last-child)]:border-r [&>*:nth-child(n+3)]:border-t
        sm:[&>*:nth-child(n+3)]:border-t-0 [&>*:nth-child(odd)]:border-r`,
        `[&>*]:border-gray-200 [&>*]:outline-2 focus-within:[&>*]:relative
        focus-within:[&>*]:z-10 focus-within:[&>*]:outline`
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const fd = Object.fromEntries(
          new FormData(e.target as HTMLFormElement)
        );
        if (!fd.address) return;
        try {
          const {
            data: { latitude: lat, longitude: lng },
          } = await axios_demand.post<IGeoDirection>(`/geo/direction`, fd);
          map.setView({ lat, lng }, 12);
          setPoint({ lat, lng });
        } catch (_error) {
          setAlert(t('common:msg.noDirection'), 'warning', 5000);
        }
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {enableSearch && (
        <>
          {countries && (
            <RSelect
              className="sPrS-container basis-1/2 text-gray-800 sm:basis-auto"
              classNamePrefix="sPrS"
              name="country_id"
              options={countries}
              isDisabled={readOnlyCountry}
              getOptionValue={(c) => `${c.id}`}
              getOptionLabel={(c) => c.name ?? ''}
              defaultValue={defaultCountry}
              onChange={(e) => {
                setSelectedCountry((e?.id as 1) || 1);
                const abbr = idToCountry[e?.id as 1] ?? country;
                const defaultCityByCountry = points[abbr];
                const point = {
                  lat: defaultCityByCountry?.center.lat,
                  lng: defaultCityByCountry?.center.lng,
                };
                map?.setView(point, defaultCityByCountry.zoom);
                setSearch('');
              }}
              isLoading={!countries}
              components={{ IndicatorSeparator: () => null }}
            />
          )}
          {defaultCity && (
            <RSelect
              key={defaultCity.at(0)?.id ?? 'unknown'}
              classNamePrefix="sPrS"
              className="sPrS-container basis-1/2 text-gray-800 sm:basis-auto"
              name="city_id"
              isSearchable
              inputValue={citySearch}
              onInputChange={(v, meta) => {
                if (
                  // https://komelin.com/blog/developing-custom-search-box-with-react-select
                  meta.action !== 'input-blur' &&
                  meta.action !== 'menu-close'
                )
                  setCitySearch(v);
              }}
              options={cities}
              getOptionValue={(c) => `${c.id}`}
              getOptionLabel={(c) => c.city}
              defaultValue={defaultCity?.at(0)}
              onChange={(e) => {
                if (!e) return;
                map.setView(
                  {
                    lat: Number(e.latitude),
                    lng: Number(e?.longitude),
                  },
                  12
                );
                setCityId(e.id);
                setSearch('');
                onGetSearch?.({ cityId: e.id, address: '' });
              }}
              isLoading={!cities && citySearch.trim().length >= 3}
              components={{ IndicatorSeparator: () => null }}
              noOptionsMessage={({ inputValue }) => {
                if (inputValue.trim().length < 3)
                  return t('common:msg.writeSearch');
                return t('common:msg.noResults');
              }}
            />
          )}
          <input
            type="text"
            name="address"
            className="flex-grow rounded-bl bg-white p-2 text-gray-800 sm:flex-grow-0
              sm:rounded-bl-none"
            value={search}
            onChange={({ target: { value } }) => {
              setSearch(value || '');
              onGetSearch?.({ cityId, address: value });
            }}
            placeholder={t('common:msg.searchPlaceholder')}
            autoFocus
          />
          <button type="submit" className="w-9 bg-white">
            <i
              className="relative -mt-1 ml-2 box-border block h-4 w-4 rounded-full border-2
                border-current text-gray-500 after:absolute after:left-3 after:top-[10px]
                after:box-border after:block after:h-2 after:w-[2px] after:-rotate-45
                after:transform after:rounded-[3px] after:bg-current"
            />
            <span className="sr-only">Search Address</span>
          </button>
        </>
      )}
      {onAction && (
        <button
          type="button"
          className="w-9 basis-9 bg-white sm:basis-auto"
          onClick={() => onAction(map.getCenter())}
        >
          <i
            className="relative ml-2 box-border block h-0.5 w-4 rounded-[10px] bg-current text-gray-500
              after:absolute after:left-[7px] after:top-[-7px] after:box-border after:block
              after:h-4 after:w-0.5 after:rounded-[10px] after:bg-current"
          />
        </button>
      )}
    </form>,
    tlNode as Element,
    'searchBox'
  );
};
